/* eslint-disable react-hooks/exhaustive-deps */
import {
  Checkbox,
  FormControlLabel,
  FormGroup,
  Grid,
  InputAdornment,
  MenuItem,
} from "@mui/material";
import { FormDatePicker, FormTextField } from "components/atoms";
import { CommonModal, FormContainer } from "components/molecules";
import { FormikHelpers, useFormik, useFormikContext } from "formik";
import { ICreatePalletsRequest } from "model";
import moment from "moment";
import { useEffect } from "react";
import { PalletActions } from "redux/actions";
import {
  useAppDispatch,
  useAppSelector,
} from "redux/configurations/reduxHooks";
import { I18nTranslator, translations } from "utils/i18n";
import * as yup from "yup";

const validateSchema = yup.object().shape({
  type: yup.string().required(translations.yupError.required),
  startDate: yup.string().required(translations.yupError.required),
  usageTime: yup
    .number()
    .min(1, "Ít nhất là 1 tháng")
    .max(120, "Không quá 10 năm")
    .required(translations.yupError.required),
  expiryDate: yup.string().required(translations.yupError.required),
  qty: yup
    .number()
    .min(1, "Phải có ít nhất 1 Pallet")
    .max(10000, "Số lượng Pallet không quá 10000")
    .required(translations.yupError.required),
});

enum EPalletType {
  WOOD = "PW",
  PLAT_PLASTIC = "PP",
  HOLE_PLASTIC = "PF",
  ALUMINUM = "PA",
}

const ModalAddPallet = (props: {
  open: boolean;
  isAdd: boolean;
  handleClose(): void;
}) => {
  const { open, isAdd, handleClose } = props;
  const dispatch = useAppDispatch();
  const action = useAppSelector((state) => state.PalletReducer.action);
  const initialValues = {
    type: EPalletType.WOOD,
    startDate: moment(),
    usageTime: 1,
    expiryDate: moment().add(1, "month"),
    qty: 0,
    exportPallet: true,
  } as ICreatePalletsRequest;

  const formik = useFormik({
    initialValues,
    enableReinitialize: isAdd ? false : true,
    validationSchema: validateSchema,
    onSubmit: (
      values: ICreatePalletsRequest,
      { validateForm }: FormikHelpers<ICreatePalletsRequest>
    ) => {
      validateForm().then(() => {
        const createRequest: ICreatePalletsRequest = {
          ...values,
          startDate: moment(values.startDate).format("YYYY-MM-DDT00:00:00"),
          expiryDate: moment(values.expiryDate).format("YYYY-MM-DDT00:00:00"),
        };
        _createPallets(createRequest);
      });
    },
  });

  const _createPallets = (request: ICreatePalletsRequest) => {
    dispatch(PalletActions.createPallets.request(request));
  };

  const handleStartDateOrUsedTimeChange = () => {
    const { startDate, usageTime } = formik.values;
    formik.setFieldValue(
      "expiryDate",
      moment(startDate).add(usageTime, "month")
    );
  };

  useEffect(() => {
    if (action === PalletActions.createPallets.successName) {
      handleClose();
    }
  }, [action]);

  useEffect(() => {
    handleStartDateOrUsedTimeChange();
  }, [formik.values.startDate, formik.values.usageTime]);

  return (
    <CommonModal
      open={open}
      title={I18nTranslator(translations.masterData.addNewPallet)}
      handleClose={handleClose}
    >
      <FormContainer
        formik={formik}
        loading={action.includes("_REQUEST")}
        isAdd={isAdd}
      >
        <Form />
      </FormContainer>
    </CommonModal>
  );
};

const Form = () => {
  return (
    <Grid container rowSpacing={2}>
      <FormGeneralInformation />
    </Grid>
  );
};

const FormGeneralInformation = () => {
  const context = useFormikContext();
  return (
    <>
      <Grid xs={12} item>
        <FormTextField
          label={I18nTranslator(translations.masterData.palletType)}
          select
          id="type"
        >
          <MenuItem value={EPalletType.WOOD}>
            {I18nTranslator(translations.type.wood)}
          </MenuItem>
          <MenuItem value={EPalletType.PLAT_PLASTIC}>
            {I18nTranslator(translations.type.flatPlastic)}
          </MenuItem>
          <MenuItem value={EPalletType.HOLE_PLASTIC}>
            {I18nTranslator(translations.type.holePlastic)}
          </MenuItem>
          <MenuItem value={EPalletType.ALUMINUM}>
            {I18nTranslator(translations.type.aluminum)}
          </MenuItem>
        </FormTextField>
      </Grid>
      <Grid xs={12} container item columnSpacing={1}>
        <Grid xs={6} item>
          <FormDatePicker
            id="startDate"
            label={I18nTranslator(translations.masterData.palletStartDateUsed)}
          />
        </Grid>
        <Grid xs={6} item>
          <FormTextField
            label={I18nTranslator(translations.masterData.palletExpiryTime)}
            fullWidth
            placeholder={I18nTranslator(
              translations.placeholder.inputExpiryTime
            )}
            id="usageTime"
            type={"number"}
            InputProps={{
              endAdornment: (
                <InputAdornment position="start">
                  {I18nTranslator(translations.masterData.mount)}
                </InputAdornment>
              ),
            }}
          />
        </Grid>
      </Grid>
      <Grid xs={12} container item columnSpacing={1}>
        <Grid xs={6} item>
          <FormDatePicker
            id="expiryDate"
            label={I18nTranslator(translations.masterData.palletEndDate)}
            disabled
          />
        </Grid>
      </Grid>
      <Grid xs={12} item columnSpacing={1}>
        <FormTextField
          label={I18nTranslator(translations.masterData.palletQuantity)}
          fullWidth
          placeholder={I18nTranslator(
            translations.placeholder.inputPalletQuantity
          )}
          id="qty"
          type={"number"}
        />
      </Grid>
      <Grid xs={12} item columnSpacing={1}>
        <FormGroup>
          <FormControlLabel
            control={
              <Checkbox
                checked={(context?.values as any).exportPallet}
                onClick={() =>
                  context.setFieldValue(
                    "exportPallet",
                    !(context?.values as any).exportPallet
                  )
                }
              />
            }
            label={I18nTranslator(
              translations.button.exportAfterCreateSuccessfully
            )}
          />
        </FormGroup>
      </Grid>
    </>
  );
};

export default ModalAddPallet;
